import { Game, Match, TournamentCivAbrr, TournamentData, TournamentDataSection, TournamentDraft, TOURNAMENT_CIV_ABBR_CORRECTIONS } from "../types/tournaments";

function parseVariables(raw: string) {
  return [...raw.matchAll(/(\w+)=((?:\{+[^}]+\}+|[^{}|\r\n]+)*)/g)].reduce((acc, [_, k, v]) => {
    acc[k] = v.trim();
    return acc;
  }, {} as Record<string, string>);
}

function parseCiv(raw: string) {
  var arr = raw.split(',').map(v => (TOURNAMENT_CIV_ABBR_CORRECTIONS[v] || v) as TournamentCivAbrr);
  return arr.length > 1 ? arr : arr[0];
}

export const fetchResults = async (url: string): Promise<TournamentData> => {
  return await fetch(url).then((res) => res.json());
};

export function parseMatchesFromWikiSyntax(section: TournamentDataSection): Match[] {
  const matches: Match[] = [];

  const { body, drafts } = section;

  // Parse rounds
  for (const raw of body.split(/(?<=^\}\})/gm).flatMap(d => d.split(/(?=^\{\{)/gm))) {
    if (raw.startsWith("{{GroupTableLeague"))
      continue;
    try {
      const parsed = parseVariables(raw);
      let score1 = 0;
      let score2 = 0;
      for (const [k, v] of Object.entries(parsed)) {
        if (!!k.match(/^(R\d+[DW]|opponent)\d+$/gm)) {
          const playerName = v.replace(/\s*\{+(?:Solo|Team)Opponent\|([^|}]*)\}+\s*$/, "$1");
          if (!parsed.player1) {
            parsed.player1 = playerName;
            parsed.player1Id = k;
          } else if (!parsed.player2) {
            parsed.player2 = playerName;
            parsed.player2Id = k;
          }
        } else if (!!k.match(/^map\d+/gm)) {
          const parsedMap = parseVariables(v);
          if (parsedMap.winner == "1")
            score1++;
          else if (parsedMap.winner == "2")
            score2++;
        }
      }
      if (parsed.bestof && !parsed.winner) {
        const threshold = (parseInt(parsed.bestof) + 1) / 2;
        if (score1 >= threshold) {
          parsed.winner = "1";
          parsed.finished = "true";
        } else if (score2 >= threshold) {
          parsed.winner = "2";
          parsed.finished = "true";
        }
      } else if (parsed.finished == "true" && !parsed.winner) {
        if (score1 > score2) {
          parsed.winner = "1";
        } else if (score2 > score1) {
          parsed.winner = "2";
        }
      }
      if (!parsed["player1"]) continue;
      const { player1, player1Id, player2, player2Id, vod } = parsed;
      const winner =
        player1Id && parsed[player1Id + "win"] == "1"
          ? player1
          : player2Id && parsed[player2Id + "win"] == "1"
          ? player2
          : parsed.winner == "1"
          ? player1
          : parsed.winner == "2"
          ? player2
          : null;

      const match: Match = {
        players: [parsed.player1, parsed.player2],
        score: [0, 0],
        games: [],
        vod,
        finished: !!parsed.finished,
        date: parsed.date?.split("{")[0].trim(),
        winner,
        raw: JSON.stringify(parsed, null, 2),
      };

      for (let i = 1; i < 30; i++) {
        const prefix = `map${i}`;
        // New style: map1={{Map|map=Coastal Cliffs|winner=1|civs1=ord|civs2=eng}}
        if (!!parsed[prefix] && parsed[prefix].match(/^\{\{Map/)) {
          const parsedMap = parseVariables(parsed[prefix].replace(/\}\}$/, ''));
          const { winner } = parsedMap;
          if (!winner) continue;
          if (winner == "1") match.score[0]++;
          if (winner == "2") match.score[1]++;
          const map = parsedMap.map?.trim();
          const game: Game = {
            map: map?.length ? map : "Unknown",
            played: winner && winner != "skip",
            teams: [
              {
                name: player1,
                players: parsedMap.players1 ? parsedMap.players1.split(',').map(p => p.trim()) : [player1],
                civs: parseCiv(parsedMap.civs1),
                win: winner == "1",
              },
              {
                name: player2,
                players: parsedMap.players2 ? parsedMap.players2.split(',').map(p => p.trim()) : [player2],
                civs: parseCiv(parsedMap.civs2),
                win: winner == "2",
              },
            ],
          };
          if (game.played) match.games.push(game);
          continue;
        }

        // Old style
        if (!parsed[prefix + "win"]) continue;
        const winner = parsed[prefix + "win"];
        if (winner == "1") match.score[0]++;
        if (winner == "2") match.score[1]++;
        const map = parsed[prefix].split("}").pop()?.trim();
        const game: Game = {
          map: map?.length ? map : "Unknown",
          played: winner && winner != "skip",
          teams: [
            {
              name: player1,
              players: [player1],
              civs: parseCiv(parsed[prefix + "p1civ"]),
              win: winner == "1",
            },
            {
              name: player2,
              players: [player2],
              civs: parseCiv(parsed[prefix + "p2civ"]),
              win: winner == "2",
            },
          ],
        };
        if (game.played) match.games.push(game);
      }
      if (match.finished) matches.push(match);
      if (parsed["mapdraft"]) {
        const draft = drafts[parsed["mapdraft"]];
        match.map_bans = draft.bans;
      }
      if (parsed["draft"]) {
        const draft = drafts[parsed["draft"]];
        match.civ_bans = draft.bans;
      }
      if (parsed["civdraft"]) {
        const draft = drafts[parsed["civdraft"]];
        match.civ_bans = draft.bans;
      }
      if (parsed["civdraft2"]) {
        const draft = drafts[parsed["civdraft2"]];
        match.civ_bans = match.civ_bans ? match.civ_bans.concat(draft.bans) : draft.bans;
      }
      // Fix it if drafts were swapped
      if (match.map_bans?.length && match.map_bans[0].startsWith("aoe4")) {
        const real_civ_bans = match.map_bans;
        match.map_bans = match.civ_bans;
        match.civ_bans = real_civ_bans;
      }
    } catch (e) {
      console.error("Error parsing match", raw, e);
    }
  }

  return matches;
}
